import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ShoppingBag, Minus, Plus, ChevronLeft, Truck, ShieldCheck, Sparkles } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Reveal } from "@/components/Reveal";
import { NotesPyramid } from "@/components/NotesPyramid";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, isWished } = useStore();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setProduct(null); setNotFound(false); setQty(1); setActiveImg(0);
    api.getProduct(id).then((p) => {
      setProduct(p);
      api.getProducts().then((list) => setRelated(list.filter((x) => x.id !== p.id).slice(0, 8)));
    }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-cream">
        <p className="font-serif text-3xl text-ink">Parfumul nu a fost găsit</p>
        <Link to="/" className="text-mocha-dark underline">Înapoi la colecție</Link>
      </div>
    );
  }
  if (!product) return <div className="min-h-screen bg-cream" />;

  const gallery = (product.images && product.images.length) ? product.images : (product.image ? [product.image] : []);
  const mainImg = gallery[activeImg] || product.image;

  return (
    <main data-testid="product-detail-page" className="bg-cream pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="flex items-center gap-2 text-ink-soft hover:text-ink transition-colors mb-8 text-sm">
          <ChevronLeft size={18} /> Înapoi
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-10 lg:gap-16 items-start">
          <div className="flex flex-col gap-4 w-full max-w-sm mx-auto lg:mx-0 lg:sticky lg:top-28">
            <motion.div key={mainImg} initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative aspect-[3/4] overflow-hidden bg-beige">
              <img src={mainImg} alt={product.name} className="w-full h-full object-cover" />
              {product.discount_pct ? (
                <span className="absolute top-4 left-4 bg-ink text-[#EBC98A] text-[11px] uppercase tracking-widest px-3 py-1.5 font-bold rounded-full shadow-md">−{product.discount_pct}%</span>
              ) : null}
            </motion.div>
            {gallery.length > 1 && (
              <div data-testid="detail-gallery-thumbs" className="flex gap-3 flex-wrap">
                {gallery.map((src, i) => (
                  <button key={src} data-testid={`detail-thumb-${i}`} onClick={() => setActiveImg(i)}
                    className={`w-16 h-20 sm:w-20 sm:h-24 overflow-hidden bg-beige border transition-all ${i === activeImg ? "border-mocha-dark ring-1 ring-mocha-dark" : "border-line hover:border-mocha"}`}>
                    <img src={src} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col">
            <Reveal>
              <p className="eyebrow text-mocha-dark mb-3">{product.brand} · {product.category}</p>
              <h1 className="font-serif font-light text-5xl lg:text-6xl text-ink leading-[1.05]">{product.name}</h1>
              <div className="flex items-baseline gap-3 mt-6">
                <span className="text-3xl text-ink font-medium">{formatPrice(product.price)}</span>
                {product.original_price ? <span className="text-ink-muted text-xl line-through">{formatPrice(product.original_price)}</span> : null}
              </div>
              <div className="mt-3">
                {product.stock <= 0 ? (
                  <span className="text-sm uppercase tracking-widest text-[#C1362F] font-semibold">Sold Out · Indisponibil</span>
                ) : product.stock <= 3 ? (
                  <span className="text-sm uppercase tracking-widest text-mocha-dark font-semibold">Stoc redus · doar {product.stock} bucăți</span>
                ) : (
                  <span className="text-sm uppercase tracking-widest text-ink-muted">În stoc</span>
                )}
              </div>
              {product.description ? <p className="text-ink-soft leading-relaxed mt-6 max-w-lg">{product.description}</p> : null}

              <div className="mt-8">
                <NotesPyramid product={product} />
              </div>

              <div className="flex items-center gap-4 mt-10">
                <div className="flex items-center border border-line">
                  <button data-testid="detail-qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-12 flex items-center justify-center text-ink hover:bg-beige transition-colors"><Minus size={16} /></button>
                  <span className="w-12 text-center">{qty}</span>
                  <button data-testid="detail-qty-increase" onClick={() => setQty((q) => Math.min(product.stock || 1, q + 1))} className="w-11 h-12 flex items-center justify-center text-ink hover:bg-beige transition-colors"><Plus size={16} /></button>
                </div>
                {product.stock <= 0 ? (
                  <span data-testid="detail-sold-out" className="flex-1 flex items-center justify-center py-4 bg-ink/10 text-ink-muted text-xs uppercase tracking-[0.2em] cursor-not-allowed">Sold Out</span>
                ) : (
                  <button data-testid="detail-add-cart" onClick={() => addToCart(product, qty)}
                    className="flex-1 flex items-center justify-center gap-3 py-4 bg-ink text-cream text-xs uppercase tracking-[0.2em] hover:bg-mocha-dark transition-colors">
                    <ShoppingBag size={16} /> Adaugă în coș
                  </button>
                )}
                <button data-testid="detail-wishlist" onClick={() => toggleWishlist(product)}
                  className={`w-14 h-14 flex items-center justify-center border transition-colors ${isWished(product.id) ? "border-[#C1362F] text-[#C1362F]" : "border-line text-ink hover:border-ink"}`}>
                  <Heart size={18} strokeWidth={1.5} fill={isWished(product.id) ? "#C1362F" : "none"} />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-10 pt-8 border-t border-line">
                {[[Truck, "Livrare gratuită", "peste 350 RON"], [ShieldCheck, "Autenticitate", "100% garantată"], [Sparkles, "Ambalaj cadou", "la cerere"]].map(([Icon, t, s]) => (
                  <div key={t} className="text-center">
                    <Icon size={20} strokeWidth={1.5} className="mx-auto text-mocha-dark mb-2" />
                    <p className="text-xs text-ink font-medium">{t}</p>
                    <p className="text-[11px] text-ink-muted">{s}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-24">
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-ink text-center mb-10">S-ar putea să-ți placă</h2>
            <div className="flex gap-4 sm:gap-5 overflow-x-auto pb-4 -mx-5 px-5 sm:-mx-8 sm:px-8 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {related.map((r) => (
                <div
                  key={r.id}
                  data-testid={`related-${r.id}`}
                  onClick={() => navigate(`/parfum/${r.id}`)}
                  className="group cursor-pointer shrink-0 w-[42vw] sm:w-52 snap-start"
                >
                  <div className="relative aspect-[3/4] overflow-hidden bg-beige mb-3 rounded-[2px]">
                    <img src={r.image} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    {r.discount_pct ? (
                      <span className="absolute top-2 left-2 bg-ink text-[#EBC98A] text-[10px] tracking-[0.12em] px-2.5 py-1 font-bold rounded-full shadow-md">−{r.discount_pct}%</span>
                    ) : null}
                  </div>
                  <p className="eyebrow text-[0.55rem] text-mocha-dark">{r.brand}</p>
                  <h3 className="font-serif text-lg text-ink leading-snug">{r.name}</h3>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-ink text-sm font-semibold">{formatPrice(r.price)}</span>
                    {r.original_price ? <span className="text-ink-muted text-xs line-through">{formatPrice(r.original_price)}</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
